import Tooltip from '@/components/Tooltip';
import { FolderUp, ShieldCheck, TriangleAlert } from '@music-vine/cadence/icons';
import { Button } from '@music-vine/cadence/ui';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { uploadVideoFileAtom, videoLoadingAtom, videoUploadErrorAtom } from '../atoms/videoPlayerAtoms';
import { AnimatePresence, motion } from 'motion/react';
import { extractFileMetadata, trackVideoUploadAttempt, trackVideoUploadError } from '../utils/tracking';
import type { VideoFileMetadata } from '../utils/tracking';

// Video file validation constants
const SUPPORTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime',
// .mov files
'video/x-msvideo' // .avi files
];
function getErrorSupportText(error: string): string | null {
  if (error === 'No file detected') {
    return 'We could not find a file in your selection. Try picking a video from your device again.';
  }
  if (error === 'Invalid file format') {
    return 'Use a standard video file such as .mp4, .mov or .webm. Other formats may not be supported.';
  }
  if (error === 'Failed to load the video') {
    return 'The file may be corrupted or an unsupported codec. Try exporting again or using a different file.';
  }
  return null;
}

/**
 * File upload UI shown when no video is loaded
 * Validates file type and format
 */
export const VideoUploadPrompt = () => {
  const {
    asPath
  } = useRouter();
  const uploadVideo = useSetAtom(uploadVideoFileAtom);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const loadError = useAtomValue(videoUploadErrorAtom);
  const setLoadError = useSetAtom(videoUploadErrorAtom);
  const isLoading = useAtomValue(videoLoadingAtom);
  const [showLoadingUI, setShowLoadingUI] = useState(false);
  const lastFileMetadataRef = useRef<VideoFileMetadata | null>(null);

  // Delay showing loading UI to prevent flashing for quick loads
  useEffect(() => {
    if (isLoading) {
      const timeout = setTimeout(() => {
        setShowLoadingUI(true);
      }, 1000);
      return () => clearTimeout(timeout);
    }
    setShowLoadingUI(false);
  }, [isLoading]);

  // Use validation error if present, otherwise show load error from atom
  const error = validationError || loadError;
  const errorSupportText = error ? getErrorSupportText(error) : null;
  const validateAndUploadFile = (file: File | undefined) => {
    // Clear previous errors
    setValidationError(null);
    setLoadError(null);
    if (!file) {
      setValidationError('No file detected');
      trackVideoUploadError('No file detected', null, asPath);
      return;
    }

    // Track upload attempt with file metadata
    trackVideoUploadAttempt(file, asPath);

    // Store metadata for potential error tracking
    lastFileMetadataRef.current = extractFileMetadata(file);

    // Validate file type - check if it's a video
    if (!file.type.startsWith('video/')) {
      setValidationError('Invalid file format');
      trackVideoUploadError('Invalid file format', lastFileMetadataRef.current, asPath);
      return;
    }

    // Validate specific video format
    if (!SUPPORTED_VIDEO_TYPES.includes(file.type)) {
      setValidationError('Invalid file format');
      trackVideoUploadError('Invalid file format', lastFileMetadataRef.current, asPath);
      return;
    }

    // All validations passed, upload the video
    // uploadVideoFileAtom will clear the error atom
    try {
      uploadVideo(file);
    } catch {
      setValidationError('Failed to load the video');
      trackVideoUploadError('Failed to load the video', lastFileMetadataRef.current, asPath);
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    validateAndUploadFile(file);
    // Reset input so the same file can be selected again if needed
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    // Clear errors when user starts dragging a new file
    setValidationError(null);
    setLoadError(null);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    validateAndUploadFile(file);
  };
  return <div className="flex size-full flex-col gap-3" onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop} data-sentry-component="VideoUploadPrompt" data-sentry-source-file="VideoUploadPrompt.tsx">
      <Button className={`flex size-full grow-1 flex-col items-center gap-3 border-1 border-gray-300 border-dashed bg-gray-50 transition-colors active:scale-100 dark:border-gray-800 dark:bg-gray-900 ${isDragging ? 'border-pink-500 bg-pink-50 dark:bg-pink-950' : ''}`} onClick={() => inputRef.current?.click()} variant="subtle" data-sentry-element="Button" data-sentry-source-file="VideoUploadPrompt.tsx">
        <FolderUp className="size-10 fill-none text-pink-500" data-sentry-element="FolderUp" data-sentry-source-file="VideoUploadPrompt.tsx" />
        <div className="flex flex-col items-center gap-2 text-center">
          <h3 className="font-sans @[386px]:text-base text-gray-700 text-sm dark:text-white">
            {showLoadingUI ? <span className="font-semibold">Loading</span> : <>
                <span className="font-semibold">Add a video</span> or drag it
                here
              </>}
          </h3>
          {/* Loading progress bar */}

          <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="VideoUploadPrompt.tsx">
            {!!showLoadingUI && <motion.div animate="animate" className="h-1.5 w-32 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700" exit="exit" initial="initial" key="loading-progress" transition={{
            when: 'afterChildren',
            duration: 0.2
          }} variants={{
            initial: {
              opacity: 1
            },
            animate: {
              opacity: 1
            },
            exit: {
              opacity: 0
            }
          }}>
                <motion.div className="h-full rounded-full bg-pink-500" variants={{
              initial: {
                width: '0%'
              },
              animate: {
                width: ['30%', '60%', '90%', '91%', '92%', '92.5%'],
                transition: {
                  duration: 2
                }
              },
              exit: {
                width: '100%',
                transition: {
                  duration: 0.3
                }
              }
            }} />
              </motion.div>}
          </AnimatePresence>
          {!!error && !showLoadingUI && <div className="flex flex-col items-center gap-1.5">
              <div className="flex items-center gap-1 rounded-full bg-red-50 px-3 py-1 @[386px]:text-sm text-red-600 text-xs dark:bg-red-950 dark:text-red-400">
                <TriangleAlert className="@[386px]:size-4 size-3 fill-none" />
                {error}
              </div>
              {errorSupportText && <p className="animate-fadeIn whitespace-normal text-balance @[386px]:text-xs text-gray-700 text-xxs dark:text-gray-200">
                  {errorSupportText}
                </p>}
            </div>}
        </div>
        <input accept="video/*" className="hidden" onChange={handleFileSelect} ref={inputRef} type="file" />
      </Button>

      <div className="flex items-center justify-between">
        <p className="pr-2 @[386px]:text-sm text-xs">
          Recommended formats:{' '}
          <span className="font-semibold">.mp4, .mov, .webm</span>
        </p>
        <Tooltip label={<>
              <span className="font-semibold">100% private:</span> Videos stay
              local, never stored by us.
            </>} side="bottom" data-sentry-element="Tooltip" data-sentry-source-file="VideoUploadPrompt.tsx">
          <div className="flex size-7 justify-center rounded-full bg-green-50 p-1.5 align-center transition-all duration-300 hover:bg-green-100 dark:bg-green-950 dark:hover:bg-green-900">
            <ShieldCheck className="size-4 fill-none text-green-600 dark:text-green-400" data-sentry-element="ShieldCheck" data-sentry-source-file="VideoUploadPrompt.tsx" />
          </div>
        </Tooltip>
      </div>
    </div>;
};